// Component-free route definitions: path patterns, buildFns (`routeFns`), and
// reload-safe keys. Split out of routes.ts so that api.ts and page components
// can import `routeFns`/`rawRoutes` without pulling in the page-component graph
// (which created ~191 circular dependencies). routes.ts binds components to these.
import type { Dayjs } from "@thalamos/common";
import type {
  AuditTrailEntryType,
  MhaDashboardDataParams,
  SupportingFormsDetails,
  UUID,
  WorkItem,
} from "@thalamos/aspire-common";
import { buildFormPath } from "@thalamos/aspire-common";

function createSearchParams(params: object) {
  return new URLSearchParams(
    Object.entries(params)
      .filter(([_k, v]) => v)
      .flatMap(([key, values]) =>
        Array.isArray(values)
          ? values.map((value) => [key, value])
          : [[key, values]],
      ),
  );
}

export const rawRoutesBase = {
  home: {
    path: "/",
    exact: true,
    breadcrumb: "Home",
    buildFn: () => `/`,
  },
  demographicsManagement: {
    path: "/demographics-management",
    exact: true,
    breadcrumb: "Demographics Management",
    buildFn: () => `/demographics-management`,
  },
  testErrorPage: {
    path: "/test-error-page",
    exact: false,
    breadcrumb: "Home",
    buildFn: () => `/test-error-page`,
  },
  errorPage: {
    path: "/error-page",
    exact: false,
    breadcrumb: "Home",
    buildFn: () => `/error-page`,
  },
  visionPreviewIncident: {
    path: "/vision-incident",
    exact: false,
    breadcrumb: "Preview 136 Incident",
    buildFn: (incidentId: string) =>
      `/vision-incident?incident-id=${incidentId}`,
  },
  dashboard: {
    path: "/dashboard",
    exact: true,
    breadcrumb: "Dashboard",
    buildFn: () => `/dashboard`,
  },
  // Pathway analytics: a chromeless workspace rendered OUTSIDE the main <Layout>
  // (see App.tsx early-return), launched in its own window from the admin menu.
  // It is bound in routes.ts only to satisfy the route-key map; that table entry
  // is not what renders it.
  pathwayAnalytics: {
    path: "/pathway-analytics",
    exact: false,
    breadcrumb: "Pathway Analytics",
    buildFn: () => `/pathway-analytics`,
  },
  // Chromeless connectivity map, opened in its own window from the admin menu
  // (see App.tsx early-return). Bound in routes.ts only to satisfy the route-key
  // map; that table entry is not what renders it.
  networkMap: {
    path: "/network-map",
    exact: false,
    breadcrumb: "Network Map",
    buildFn: () => `/network-map`,
  },
  nhsDashboard: {
    path: "/nhs-dashboard",
    exact: true,
    breadcrumb: "nhs Dashboard",
    buildFn: () => `/nhs-dashboard`,
  },
  dashboardViewer: {
    path: "/dashboard/:dashboardId",
    exact: true,
    breadcrumb: "Dashboard",
    buildFn: (dashboardId: string) => `/dashboard/${dashboardId}`,
  },
  dataGlossary: {
    path: "/data-glossary/:dashboardId",
    exact: true,
    breadcrumb: "Data Glossary",
    buildFn: (dashboardId: string) => `/data-glossary/${dashboardId}`,
  },
  patientList: {
    path: "/patient-list",
    exact: true,
    breadcrumb: "Patient list",
    buildFn: () => `/patient-list`,
  },
  caseloadUser: {
    path: "/caseload/user-caseload",
    exact: true,
    breadcrumb: "User Caseload",
    buildFn: () => `/caseload/user-caseload`,
  },
  caseloadCaseload: {
    path: "/caseload/caseload",
    exact: true,
    breadcrumb: "Caseload",
    buildFn: () => `/caseload/caseload`,
  },
  caseloadArchive: {
    path: "/caseload/archive",
    exact: true,
    breadcrumb: "Archive",
    buildFn: () => `/caseload/archive`,
  },
  notifications: {
    path: "/notifications",
    exact: true,
    breadcrumb: "Notifications",
    buildFn: () => `/notifications`,
  },
  teamCreateEdit: {
    path: "/teams/:teamId/edit",
    exact: true,
    breadcrumb: "Team Edit",
    buildFn: (teamId: string) => `/teams/${teamId}/edit`,
  },
  managerTeamEdit: {
    path: "/manager/team/:teamId/edit",
    exact: true,
    breadcrumb: "Team Edit",
    buildFn: (teamId: string) => `/manager/team/${teamId}/edit`,
  },
  organisationHome: {
    path: "/organisations/:organisationId",
    exact: true,
    breadcrumb: "Organisation",
    buildFn: (orgId: string) => `/organisations/${orgId}`,
  },
  organisationCreateEdit: {
    path: "/organisations/:organisationId/edit",
    exact: true,
    breadcrumb: "Organisation Edit",
    buildFn: (orgId: string) => `/organisations/${orgId}/edit`,
  },
  PatientSearchReasoningPage: {
    path: "/patients/:patientId/search-reasoning",
    exact: true,
    breadcrumb: "Patient",
    buildFn: (patientId: string, redirectTo: string) =>
      `/patients/${patientId}/search-reasoning?redirectTo=${encodeURIComponent(redirectTo)}`,
  },
  formUploadSelectedTemplate: {
    path: "/patients/:patientId/forms/:formContextId/form-upload-select-template",
    exact: true,
    breadcrumb: "Form Upload",
    buildFn: (formId: string, patientId: string) =>
      `${buildFormPath(formId, patientId)}/form-upload-select-template`,
  },
  formUpload: {
    path: "/patients/:patientId/forms/:formContextId/form-upload/:formTemplateIdFromUrl/:supportingFormsType?",
    exact: true,
    breadcrumb: "Form Upload",
    buildFn: (
      formId: string,
      patientId: string,
      formTemplateIdFromUrl?: string,
      supportingFormsDetails?: SupportingFormsDetails | undefined,
    ) => {
      let url = `${buildFormPath(
        formId,
        patientId,
      )}/form-upload/${formTemplateIdFromUrl}`;

      if (supportingFormsDetails) {
        if (supportingFormsDetails.type === "provided") {
          const formIdsParam = encodeURIComponent(
            supportingFormsDetails.formIds.join(","),
          );
          url += `/formIds=${formIdsParam}`;
        } else if (supportingFormsDetails.type === "partially-provided") {
          const formIdsParam = encodeURIComponent(
            supportingFormsDetails.formIds.join(","),
          );
          url += `/formIds=${formIdsParam}&reason=${encodeURIComponent(supportingFormsDetails.reason)}`;
        } else if (supportingFormsDetails.type === "not-provided") {
          url += `/reason=${encodeURIComponent(supportingFormsDetails.reason)}`;
        }
      }

      return url;
    },
  },
  patientMergeSearch: {
    path: "/patients/merge/from/:fromPatientId",
    exact: true,
    breadcrumb: "Patient",
    buildFn: (fromPatientId: string) => `/patients/merge/from/${fromPatientId}`,
  },
  patientMergeConfirmation: {
    path: "/patients/merge/from/:fromPatientId/to/:toPatientId",
    exact: true,
    breadcrumb: "Patient",
    buildFn: (fromPatientId: string, toPatientId: string) =>
      `/patients/merge/from/${fromPatientId}/to/${toPatientId}`,
  },
  patientLinkToPds: {
    path: "/patients/:patientId/link-to-pds",
    exact: true,
    breadcrumb: "Patient",
    buildFn: (patientId: string) => `/patients/${patientId}/link-to-pds`,
  },
  patientSyncExternalLink: {
    path: "/patients/:patientId/sync-external-link/:externalLinkId",
    exact: true,
    breadcrumb: "Patient",
    buildFn: (patientId: string, externalLinkId: string) =>
      `/patients/${patientId}/sync-external-link/${externalLinkId}`,
  },
  patientSearch: {
    path: "/patients",
    exact: true,
    breadcrumb: "Patient Search",
    buildFn: (redirectTo?: string) =>
      `/patients${redirectTo ? `?redirectTo=${encodeURIComponent(redirectTo)}` : ""}`,
  },
  patientCreate: {
    path: "/patients/create",
    exact: true,
    breadcrumb: "Patient Create",
    buildFn: () => `/patients/create`,
  },
  patientCreateFromExternalId: {
    path: "/patients/context-launch",
    exact: true,
    breadcrumb: "Patient Context Launch",
    buildFn: (token: string, redirectTo?: string) =>
      `/patients/context-launch?token=${token}${redirectTo ? `&redirectTo=${encodeURIComponent(redirectTo)}` : ""}`,
  },
  patientHome: {
    path: "/patients/:patientId",
    exact: true,
    breadcrumb: "Patient",
    buildFn: (patientId: string) => `/patients/${patientId}`,
  },

  patientHomeActivityLog: {
    path: "/patients/:patientId/activity-log",
    exact: true,
    breadcrumb: "Form",
    buildFn: (patientId: string) => `/patients/${patientId}/activity-log`,
  },

  patientHomeReconciliation: {
    path: "/patients/:patientId/patient-reconciliation",
    exact: true,
    breadcrumb: "Patient",
    buildFn: (patientId: string) =>
      `/patients/${patientId}/patient-reconciliation`,
  },
  patientEdit: {
    path: "/patients/:patientId/edit",
    exact: true,
    breadcrumb: "Patient",
    buildFn: (patientId: string, redirectTo?: string) =>
      `/patients/${patientId}/edit${redirectTo ? `?redirectTo=${encodeURIComponent(redirectTo)}` : ""}`,
  },

  patientFormLaunch: {
    path: "/patients/:patientId/form-launch/:category/:id?",
    exact: true,
    breadcrumb: "Patient",
    buildFn: (patientId: string, category: string, id?: string) =>
      `/patients/${patientId}/form-launch/${category}${id ? `/${id}` : ""}`,
  },
  SoadSignAndSendPage: {
    path: "/patients/:patientId/forms/:formContextId/soad-sign-and-send",
    exact: true,
    breadcrumb: "Soad Sign and Send",
    buildFn: (formId: string, patientId: string) =>
      `${buildFormPath(formId, patientId)}/soad-sign-and-send`,
  },
  soadSubmitReviewPage: {
    path: "/patients/:patientId/forms/:formContextId/soad-submit-review",
    exact: true,
    breadcrumb: "Form",
    buildFn: (formId: string, patientId: string) =>
      `${buildFormPath(formId, patientId)}/soad-submit-review`,
  },
  formContextPage: {
    path: "/patients/:patientId/forms/:formContextId",
    exact: true,
    breadcrumb: "Form",
    buildFn: (formId: string, patientId: string) =>
      buildFormPath(formId, patientId),
  },
  formContextPageActivityLog: {
    path: "/patients/:patientId/forms/:formContextId/activity-log",
    exact: true,
    breadcrumb: "Form",
    buildFn: (formId: string, patientId: string) =>
      `${buildFormPath(formId, patientId)}/activity-log`,
  },
  formContextReasonForNotProceedingDialog: {
    path: "/patients/:patientId/forms/:formContextId/decline",
    exact: true,
    breadcrumb: "Form",
    buildFn: (formId: string, patientId: string) =>
      `${buildFormPath(formId, patientId)}/decline`,
  },

  formContextReopenAdmissionDialog: {
    path: "/patients/:patientId/forms/:formContextId/reopen",
    exact: true,
    breadcrumb: "Form",
    buildFn: (formId: string, patientId: string) =>
      `${buildFormPath(formId, patientId)}/reopen`,
  },
  formContextUnfinaliseDialog: {
    path: "/patients/:patientId/forms/:formContextId/unfinalise",
    exact: true,
    breadcrumb: "Form",
    buildFn: (formId: string, patientId: string) =>
      `${buildFormPath(formId, patientId)}/unfinalise`,
  },
  formContextPagePatientReconciliation: {
    path: "/patients/:patientId/forms/:formContextId/patient-reconciliation",
    exact: true,
    breadcrumb: "Form",
    buildFn: (formId: string, patientId: string) =>
      `${buildFormPath(formId, patientId)}/patient-reconciliation`,
  },
  formContextPageSendRequest: {
    path: "/patients/:patientId/forms/:formContextId/request",
    exact: true,
    breadcrumb: "Form",
    buildFn: (
      formId: string,
      patientId: string,
      params: {
        requestType?: WorkItem["requestType"] | "share";
        formId?: string;
        formPart?: string;
        title?: string;
        infoMessage?: string;
        /** If true, after sending, redirect to S12 payment request dialog */
        openS12PaymentRequest?: string;
        /** Outcome type for S12 payment request */
        s12OutcomeType?: string;
        /** Form ID for S12 payment request */
        s12FormId?: string;
        /** If "true", show the interview linking step after sending */
        linkInterview?: string;
      } = {},
    ) => {
      const base = `${buildFormPath(formId, patientId)}/request`;

      // Build query params from any provided values
      const entries: [string, string][] = [];
      if (params.requestType) entries.push(["requestType", params.requestType]);
      if (params.formId) entries.push(["formId", params.formId]);
      if (params.formPart) entries.push(["formPart", params.formPart]);
      if (params.title) entries.push(["title", params.title]);
      if (params.infoMessage) entries.push(["infoMessage", params.infoMessage]);
      if (params.openS12PaymentRequest)
        entries.push(["openS12PaymentRequest", params.openS12PaymentRequest]);
      if (params.s12OutcomeType)
        entries.push(["s12OutcomeType", params.s12OutcomeType]);
      if (params.s12FormId) entries.push(["s12FormId", params.s12FormId]);
      if (params.linkInterview)
        entries.push(["linkInterview", params.linkInterview]);

      if (entries.length > 0) {
        const query = new URLSearchParams(entries);
        return `${base}?${query.toString()}`;
      }
      return base;
    },
  },
  formContextPageSuccessDialog: {
    path: "/patients/:patientId/forms/:formContextId/success",
    exact: true,
    breadcrumb: "Form",
    buildFn: (
      formId: string,
      patientId: string,
      params: {
        title?: string;
        message?: string;
      } = {},
    ) => {
      const base = `${buildFormPath(formId, patientId)}/success`;

      if (params.title) {
        const query = new URLSearchParams(params);
        return `${base}?${query.toString()}`;
      }
      return base;
    },
  },
  formContextPageConfirmDeleteDraftDialog: {
    path: "/patients/:patientId/forms/:formContextId/confirm-delete-draft",
    exact: true,
    breadcrumb: "Form",
    buildFn: (
      formContextId: string,
      patientId: string,
      params: {
        draftFormId?: string;
      } = {},
    ) => {
      const base = `${buildFormPath(
        formContextId,
        patientId,
      )}/confirm-delete-draft`;
      if (params.draftFormId) {
        const query = new URLSearchParams(params);
        return `${base}?${query.toString()}`;
      }
      return base;
    },
  },
  formContextPageRequestAmendDialog: {
    path: "/patients/:patientId/forms/:formContextId/request-amend",
    exact: true,
    breadcrumb: "Form",
    buildFn: (
      formContextId: string,
      patientId: string,
      params: {
        formId?: string;
        formTemplateId?: string;
      } = {},
    ) => {
      const base = `${buildFormPath(formContextId, patientId)}/request-amend`;
      if (params.formId || params.formTemplateId) {
        const query = new URLSearchParams(params);
        return `${base}?${query.toString()}`;
      }
      return base;
    },
  },
  formContextPageRequestDeMinimisDialog: {
    path: "/patients/:patientId/forms/:formContextId/request-de-minimis",
    exact: true,
    breadcrumb: "Form",
    buildFn: (
      formContextId: string,
      patientId: string,
      params: {
        formId?: string;
        formTemplateId?: string;
      } = {},
    ) => {
      const base = `${buildFormPath(formContextId, patientId)}/request-de-minimis`;
      if (params.formId || params.formTemplateId) {
        const query = new URLSearchParams(params);
        return `${base}?${query.toString()}`;
      }
      return base;
    },
  },
  formContextPageRequestRedoDialog: {
    path: "/patients/:patientId/forms/:formContextId/request-redo",
    exact: true,
    breadcrumb: "Form",
    buildFn: (
      formContextId: string,
      patientId: string,
      params: {
        formId?: string;
        formTemplateId?: string;
      } = {},
    ) => {
      const base = `${buildFormPath(formContextId, patientId)}/request-redo`;
      if (params.formId || params.formTemplateId) {
        const query = new URLSearchParams(params);
        return `${base}?${query.toString()}`;
      }
      return base;
    },
  },
  formContextInterviews: {
    path: "/patients/:patientId/forms/:formContextId/interviews",
    exact: true,
    breadcrumb: "Form",
    buildFn: (formContextId: string, patientId: string) =>
      `${buildFormPath(formContextId, patientId)}/interviews`,
  },
  formContextFindDoctors: {
    path: "/patients/:patientId/forms/:formContextId/interviews/:interviewId/find-doctors",
    exact: true,
    breadcrumb: "Form",
    buildFn: (formContextId: string, patientId: string, interviewId: string) =>
      `${buildFormPath(formContextId, patientId)}/interviews/${interviewId}/find-doctors`,
  },
  formContextS12PaymentRequest: {
    path: "/patients/:patientId/forms/:formContextId/s12-payment-request",
    exact: true,
    breadcrumb: "Form",
    buildFn: (
      formContextId: string,
      patientId: string,
      params: {
        outcomeType?: string;
        formId?: string;
      } = {},
    ) => {
      const base = `${buildFormPath(formContextId, patientId)}/s12-payment-request`;
      if (Object.keys(params).length > 0) {
        const query = new URLSearchParams(params);
        return `${base}?${query.toString()}`;
      }
      return base;
    },
  },
  formContextS12PaymentRequestReview: {
    path: "/patients/:patientId/forms/:formContextId/s12-payment-request-review",
    exact: true,
    breadcrumb: "Form",
    buildFn: (formContextId: string, patientId: string) =>
      `${buildFormPath(formContextId, patientId)}/s12-payment-request-review`,
  },
  formContextPageConfirmFinaliseDialog: {
    path: "/patients/:patientId/forms/:formContextId/confirm-finalise",
    exact: true,
    breadcrumb: "Form",
    buildFn: (
      formId: string,
      patientId: string,
      params: {
        itemName?: string;
      } = {},
    ) => {
      const base = `${buildFormPath(formId, patientId)}/confirm-finalise`;
      if (params.itemName) {
        const query = new URLSearchParams(params);
        return `${base}?${query.toString()}`;
      }
      return base;
    },
  },
  formContextPageEditMhaStatusDialog: {
    path: "/patients/:patientId/forms/:formContextId/confirm-mha-status-finalise",
    exact: true,
    breadcrumb: "Form",
    buildFn: (
      formId: string,
      patientId: string,
      params: {
        itemName?: string;
      } = {},
    ) => {
      const base = `${buildFormPath(formId, patientId)}/confirm-mha-status-finalise`;
      if (params.itemName) {
        const query = new URLSearchParams(params);
        return `${base}?${query.toString()}`;
      }
      return base;
    },
  },
  formPageLaunchLinkedForm: {
    path: "/patients/:patientId/forms/:formContextId/launch-linked-form",
    exact: true,
    breadcrumb: "Form",
    buildFn: (formId: string, patientId: string) =>
      `${buildFormPath(formId, patientId)}/launch-linked-form`,
  },
  formDraftsComplete: {
    path: "/patients/:patientId/formdrafts/:formDraftId",
    exact: true,
    breadcrumb: "Form Drafts",
    buildFn: (formDraftId: string, patientId: string) =>
      `/patients/${patientId}/formdrafts/${formDraftId}`,
  },
  formDraftsSign: {
    path: "/patients/:patientId/formdrafts/:formDraftId/sign",
    exact: true,
    breadcrumb: "Sign",
    buildFn: (formDraftId: string, patientId: string) =>
      `/patients/${patientId}/formdrafts/${formDraftId}/sign`,
  },
  formReview: {
    path: "/patients/:patientId/forms/:formContextId/review",
    exact: true,
    breadcrumb: "Review",
    buildFn: (formDraftId: string, patientId: string) =>
      `${buildFormPath(formDraftId, patientId)}/review`,
  },
  formRedoReview: {
    path: "/patients/:patientId/forms/:formContextId/redo-review",
    exact: true,
    breadcrumb: "Review Redo",
    buildFn: (formContextId: string, patientId: string) =>
      `${buildFormPath(formContextId, patientId)}/redo-review`,
  },
  notFound: {
    path: "/not-found",
    exact: false,
    breadcrumb: "Not Found",
    buildFn: () => `/not-found`,
  },
  calendarCallbackPage: {
    path: "/profile/calendar-callback",
    exact: true,
    breadcrumb: "Calendar Callback",
    buildFn: () => `/profile/calendar-callback`,
  },
  userProfilePage: {
    path: "/profile/:userId",
    exact: true,
    breadcrumb: "Profile",
    buildFn: (id: string) => `/profile/${id}`,
  },
  userMembershipsPage: {
    path: "/profile/:userId/memberships",
    exact: true,
    breadcrumb: "User Memberships",
    buildFn: (id: string) => `/profile/${id}/memberships`,
  },
  userDeviceSessionsPage: {
    path: "/profile/:userId/devices",
    exact: true,
    breadcrumb: "User Devices",
    buildFn: (userId: string) => `/profile/${userId}/devices`,
  },
  userOrganisationConfigurationPage: {
    path: "/profile/:userId/organisation-configuration",
    exact: true,
    breadcrumb: "Organisation Settings",
    buildFn: (userId: string) =>
      `/profile/${userId}/organisation-configuration`,
  },
  favouriteRecipientsPage: {
    path: "/profile/:userId/favourite-recipients",
    exact: true,
    breadcrumb: "Favourite Recipients",
    buildFn: (id: string) => `/profile/${id}/favourite-recipients`,
  },
  interviewInviteResponsePage: {
    path: "/interview-invite/:shareCode",
    exact: true,
    breadcrumb: "Assessment Invite",
    buildFn: (shareCode: string) => `/interview-invite/${shareCode}`,
  },
  myPaymentRequestsPage: {
    path: "/payment-requests",
    exact: true,
    breadcrumb: "My Payment Requests",
    buildFn: () => `/payment-requests`,
  },
  paymentReviewerPage: {
    path: "/payment-reviewer",
    exact: true,
    breadcrumb: "Payment Requests",
    buildFn: () => `/payment-reviewer`,
  },
  adminConverGuestUserPage: {
    path: "/admin/convert-guest/:userId",
    exact: true,
    breadcrumb: "Convert Guest User",
    buildFn: (id: string) => `/admin/convert-guest/${id}`,
  },
  adminApprovedEmailDomains: {
    path: "/admin/approved-email-domains",
    exact: true,
    breadcrumb: "Thalamos Admin",
    buildFn: () => `/admin/approved-email-domains`,
  },
  adminEmailRedirects: {
    path: "/admin/email-redirects",
    exact: true,
    breadcrumb: "Thalamos Admin",
    buildFn: () => `/admin/email-redirects`,
  },
  adminTeamMerge: {
    path: "/admin/team-merge",
    exact: true,
    breadcrumb: "Thalamos Admin",
    buildFn: () => `/admin/team-merge`,
  },
  adminAuditTrailListsPage: {
    path: "/admin/lists",
    exact: true,
    breadcrumb: "Thalamos Admin",
    buildFn: () => `/admin/lists`,
  },
  adminAuditTrailCurrentUserPage: {
    path: "/admin/audit/current-user",
    exact: true,
    breadcrumb: "Thalamos Admin",
    buildFn: () => `/admin/audit/current-user`,
  },
  adminAuditTrailResourcePage: {
    path: "/admin/audit/resource",
    exact: true,
    breadcrumb: "Thalamos Admin",
    buildFn: (params: {
      resourceId?: string | null;
      userId?: string | null;
      type?: AuditTrailEntryType[] | null;
      fromDate: Dayjs | null;
      toDate: Dayjs | null;
    }) => {
      const q = createSearchParams({
        ...params,
        fromDate: params.fromDate?.format("YYYY-MM-DD"),
        toDate: params.toDate?.format("YYYY-MM-DD"),
      });

      return `/admin/audit/resource${q.size > 0 ? `?${q.toString()}` : ""}`;
    },
  },
  adminBulkUsersPage: {
    path: "/admin/bulk-configure-users",
    exact: true,
    breadcrumb: "Thalamos Admin",
    buildFn: () => `/admin/bulk-configure-users`,
  },
  adminBulkCreateUsersPage: {
    path: "/admin/bulk-create-users",
    exact: true,
    breadcrumb: "Thalamos Admin",
    buildFn: () => `/admin/bulk-create-users`,
  },
  managerUsersPage: {
    path: "/manager/users",
    exact: true,
    breadcrumb: "Thalamos Admin",
    buildFn: () => `/manager/users`,
  },
  managerTeamsPage: {
    path: "/manager/teams",
    exact: true,
    breadcrumb: "Thalamos Admin",
    buildFn: () => `/manager/teams`,
  },
  adminUsersPage: {
    path: "/admin/users",
    exact: true,
    breadcrumb: "Thalamos Admin",
    buildFn: () => `/admin/users`,
  },
  adminCreateUsersPage: {
    path: "/admin/users/create",
    exact: true,
    breadcrumb: "Create User",
    buildFn: () => `/admin/users/create`,
  },
  adminTeamsPage: {
    path: "/admin/teams",
    exact: true,
    breadcrumb: "Thalamos Admin",
    buildFn: () => `/admin/teams`,
  },
  adminOrganisationsPage: {
    path: "/admin/organisations",
    exact: true,
    breadcrumb: "Thalamos Admin",
    buildFn: () => `/admin/organisations`,
  },
  adminDataPermissionsPage: {
    path: "/admin/data-permissions",
    exact: true,
    breadcrumb: "Thalamos Admin",
    buildFn: () => `/admin/data-permissions`,
  },
  adminPatientDetailsPage: {
    path: "/admin/patient-details",
    exact: true,
    breadcrumb: "Thalamos Admin",
    buildFn: () => `/admin/patient-details`,
  },
  loginPage: {
    path: "/login",
    exact: true,
    breadcrumb: "Login",
    buildFn: (returnTo?: string) => {
      if (!returnTo) return "/login";
      const newQs = new URLSearchParams();

      newQs.set("returnTo", returnTo);

      return `/login?${newQs.toString()}`;
    },
  },
  residentPage: {
    path: "/mca/residents/:patientId",
    exact: true,
    breadcrumb: "Resident",
    buildFn: (patientId: string) => `/mca/residents/${patientId}`,
  },
  careHomeRegister: {
    path: "/mca/residents",
    exact: true,
    breadcrumb: "Residents",
    buildFn: () => `/mca/residents`,
  },
  mhaStatusDashboardPage: {
    path: "/mha-status-dashboard",
    exact: true,
    breadcrumb: "MHA Status Dashboard",
    buildFn: (
      page: number,
      params: Omit<MhaDashboardDataParams, "limit" | "offset">,
    ) => {
      const newQs = new URLSearchParams();

      newQs.set("page", page.toString());

      if (params.statuses) {
        newQs.set("status", params.statuses.join(","));
      }

      if (params.expiryLessThan) {
        newQs.set("expiryLessThan", params.expiryLessThan);
      }

      if (params.trustIds && params.trustIds.length > 0) {
        newQs.set("trustIds", params.trustIds.join(","));
      }

      if (params.hospitalIds && params.hospitalIds.length > 0) {
        newQs.set("hospitalIds", params.hospitalIds.join(","));
      }

      if (params.expiryLessThan) {
        newQs.set("expiryLessThan", params.expiryLessThan);
      }

      return `/mha-status-dashboard?${newQs.toString()}`;
    },
  },
  emhFormsShareCodePage: {
    path: "/mhf",
    exact: true,
    breadcrumb: "MHF Forms Share Code",

    // Yuck
    buildFn: (shareCode?: string) =>
      shareCode ? `/mhf?shareCode=${shareCode}` : "/mhf",
  },
  guestUserLogin: {
    path: "/guest-login",
    exact: true,
    breadcrumb: "Guest Login",

    // Yuck
    buildFn: (userId?: string, returnTo?: string) =>
      returnTo
        ? userId
          ? `/guest-login?userId=${userId}&returnTo=${encodeURIComponent(returnTo)}`
          : "/guest-login"
        : "/guest-login",
  },
  patientEditDialog: {
    path: "/patients/:patientId/formdrafts/:formDraftId?patientedit=true",
    exact: true,
    breadcrumb: "Form Drafts",
    buildFn: (formDraftId: string, patientId: string) =>
      `/patients/${patientId}/formdrafts/${formDraftId}?patientedit=true`,
  },
  formContextAddFileNoteDialog: {
    path: "/patients/:patientId/forms/:formContextId/add-file-note",
    exact: true,
    breadcrumb: "Form",
    buildFn: (
      formContextId: string,
      patientId: string,
      params: {
        formId?: string;
        formTemplateId?: string;
      } = {},
    ) => {
      const base = `${buildFormPath(formContextId, patientId)}/add-file-note`;
      if (params.formId || params.formTemplateId) {
        const query = new URLSearchParams(params);
        return `${base}?${query.toString()}`;
      }
      return base;
    },
  },
  formContextDeleteFileNoteDialog: {
    path: "/patients/:patientId/forms/:formContextId/delete-file-note",
    exact: true,
    breadcrumb: "Form",
    buildFn: (
      formContextId: string,
      patientId: string,
      params: {
        formId?: string;
        formTemplateId?: string;
      } = {},
    ) => {
      const base = `${buildFormPath(
        formContextId,
        patientId,
      )}/delete-file-note`;
      if (params.formId || params.formTemplateId) {
        const query = new URLSearchParams(params);
        return `${base}?${query.toString()}`;
      }
      return base;
    },
  },

  formContextDeleteUploadedFormDialog: {
    path: "/patients/:patientId/forms/:formContextId/delete-uploaded-form",
    exact: true,
    breadcrumb: "Form",
    buildFn: (
      formContextId: string,
      patientId: string,
      params: {
        formId?: string;
        formTemplateId?: string;
      } = {},
    ) => {
      const base = `${buildFormPath(
        formContextId,
        patientId,
      )}/delete-uploaded-form`;
      if (params.formId || params.formTemplateId) {
        const query = new URLSearchParams(params);
        return `${base}?${query.toString()}`;
      }
      return base;
    },
  },

  patientEditDialogSuccess: {
    path: "/patients/:patientId/formdrafts/:formDraftId?patienteditsuccess=true",
    exact: true,
    breadcrumb: "Form Drafts",
    buildFn: (formDraftId: string, patientId: string) =>
      `/patients/${patientId}/formdrafts/${formDraftId}?patienteditsuccess=true`,
  },

  adminRioInstancesPage: {
    path: "/admin/rio-instances",
    exact: true,
    breadcrumb: "Thalamos Admin",
    buildFn: () => `/admin/rio-instances`,
  },
  rioInstanceCreateEdit: {
    path: "/rio-instances/:rioInstanceId/edit",
    exact: true,
    breadcrumb: "Rio Instance Edit",
    buildFn: (rioInstanceId: string) => `/rio-instances/${rioInstanceId}/edit`,
  },
  adminTppInstancesPage: {
    path: "/admin/tpp-instances",
    exact: true,
    breadcrumb: "Thalamos Admin",
    buildFn: () => `/admin/tpp-instances`,
  },
  tppInstanceCreateEdit: {
    path: "/tpp-instances/:tppInstanceId/edit",
    exact: true,
    breadcrumb: "TPP Instance Edit",
    buildFn: (tppInstanceId: string) => `/tpp-instances/${tppInstanceId}/edit`,
  },
  adminGenericInstancesPage: {
    path: "/admin/generic-instances",
    exact: true,
    breadcrumb: "Thalamos Admin",
    buildFn: () => `/admin/generic-instances`,
  },
  genericInstanceCreateEdit: {
    path: "/generic-instances/:genericInstanceId/edit",
    exact: true,
    breadcrumb: "Generic Instance Edit",
    buildFn: (genericInstanceId: string) =>
      `/generic-instances/${genericInstanceId}/edit`,
  },
  adminEprUserLinksPage: {
    path: "/admin/epr-user-links",
    exact: true,
    breadcrumb: "Thalamos Admin",
    buildFn: () => `/admin/epr-user-links`,
  },
  adminSupersetDashboardsPage: {
    path: "/admin/superset-dashboards",
    exact: true,
    breadcrumb: "Thalamos Admin",
    buildFn: () => `/admin/superset-dashboards`,
  },
  supersetDashboardCreateEdit: {
    path: "/superset-dashboards/:dashboardId/edit",
    exact: true,
    breadcrumb: "Superset Dashboard Edit",
    buildFn: (dashboardId: string) =>
      `/superset-dashboards/${dashboardId}/edit`,
  },
  useSingleUseActionToken: {
    path: "/single-use/:action/:token",
    exact: true,
    breadcrumb: "Single Use Action",
    buildFn: (action: string, token: UUID, teamId: UUID | null) =>
      teamId
        ? `/single-use/${action}/${token}?context-id=${teamId}`
        : `/single-use/${action}/${token}`,
  },
  singleUseActionTokensPage: {
    path: "/admin/single-use-action-tokens",
    exact: true,
    breadcrumb: "Thalamos Admin",
    buildFn: () => `/admin/single-use-action-tokens`,
  },
  adminSection12SpecialismsPage: {
    path: "/admin/section12-specialisms",
    exact: true,
    breadcrumb: "Thalamos Admin",
    buildFn: () => `/admin/section12-specialisms`,
  },
  adminSection12LanguagesPage: {
    path: "/admin/section12-languages",
    exact: true,
    breadcrumb: "Thalamos Admin",
    buildFn: () => `/admin/section12-languages`,
  },
  adminSection12PaymentRequestsPage: {
    path: "/admin/section12-payment-requests",
    exact: true,
    breadcrumb: "Thalamos Admin",
    buildFn: () => `/admin/section12-payment-requests`,
  },
  adminSection12DoctorsPage: {
    path: "/admin/section12-doctors",
    exact: true,
    breadcrumb: "Thalamos Admin",
    buildFn: () => `/admin/section12-doctors`,
  },
  adminIcbsPage: {
    path: "/admin/icbs",
    exact: true,
    breadcrumb: "Thalamos Admin",
    buildFn: () => `/admin/icbs`,
  },
  adminLocalAuthorityPaymentRatesPage: {
    path: "/admin/local-authority-payment-rates",
    exact: true,
    breadcrumb: "Thalamos Admin",
    buildFn: () => `/admin/local-authority-payment-rates`,
  },
  pdsConfiguration: {
    path: "/admin/pds-configuration",
    exact: true,
    breadcrumb: "Thalamos Admin - PDS",
    buildFn: () => `/admin/pds-configuration`,
  },
  rafConfiguration: {
    path: "/admin/raf-configuration",
    exact: true,
    breadcrumb: "Thalamos Admin - RAF",
    buildFn: () => `/admin/raf-configuration`,
  },
  s12ApprovalConfiguration: {
    path: "/admin/s12-approval-configuration",
    exact: true,
    breadcrumb: "Thalamos Admin - S12 Approval",
    buildFn: () => `/admin/s12-approval-configuration`,
  },
  odsConfiguration: {
    path: "/admin/ods-configuration",
    exact: true,
    breadcrumb: "Thalamos Admin - ODS",
    buildFn: () => `/admin/ods-configuration`,
  },
  xeroConfiguration: {
    path: "/admin/xero-configuration",
    exact: true,
    breadcrumb: "Thalamos Admin - Xero",
    buildFn: () => `/admin/xero-configuration`,
  },
  dataSharingPartershipPage: {
    path: "/admin/data-sharing-partnerships",
    exact: true,
    breadcrumb: "Thalamos Admin",
    buildFn: () => `/admin/data-sharing-partnerships`,
  },
  placeOfSafetyManagementPage: {
    path: "/admin/place-of-safety",
    exact: true,
    breadcrumb: "Thalamos Admin",
    buildFn: () => `/admin/place-of-safety`,
  },
  adminTrustsPage: {
    path: "/admin/trusts",
    exact: true,
    breadcrumb: "Thalamos Admin",
    buildFn: () => `/admin/trusts`,
  },
  adminMentalHealthCareProvidersPage: {
    path: "/admin/mental-health-care-providers",
    exact: true,
    breadcrumb: "Thalamos Admin",
    buildFn: () => `/admin/mental-health-care-providers`,
  },

  termsAndConditionsManagementPage: {
    path: "/admin/terms-and-conditions",
    exact: true,
    breadcrumb: "Thalamos Admin",
    buildFn: () => `/admin/terms-and-conditions`,
  },
  termsAndConditionsCreateEdit: {
    path: "/admin/terms-and-conditions/:termsAndConditionsId/edit",
    exact: true,
    breadcrumb: "Thalamos Admin",
    buildFn: (termsAndConditionsId: string) =>
      `/admin/terms-and-conditions/${termsAndConditionsId}/edit`,
  },
  editMhaStatusPage: {
    path: "/patients/:patientId/mha-status/",
    exact: true,
    breadcrumb: "Edit MHA Status",
    buildFn: (patientId: string) => `/patients/${patientId}/mha-status/`,
  },
  newSoadAssessment: {
    path: "/soad/new",
    exact: true,
    breadcrumb: "Begin SOAD Assessment",
    buildFn: () => `/soad/new`,
  },
  soadReportFormDraftCompletionPage: {
    path: "/patients/:patientId/forms/:formContextId/soad-report-form/complete/:sectionName",
    exact: true,
    breadcrumb: "Soad Report Form",
    buildFn: (
      formContextId: string,
      patientId: string,
      sectionName:
        | "assessmentDetails"
        | "diagnosisAndHistory"
        | "currentAndProposedTreatment"
        | "consulteeDetails"
        | "soadAuthorisation"
        | "mha-soad-t2"
        | "mha-soad-t3"
        | "mha-soad-t4"
        | "mha-soad-t5"
        | "mha-soad-t6"
        | "mha-soad-cto11"
        | "mha-soad-nocert",
    ) =>
      `/patients/${patientId}/forms/${formContextId}/soad-report-form/complete/${sectionName}`,
  },
  londonCareRecordConfigurationPage: {
    path: "/admin/london-care-record-configuration",
    exact: true,
    breadcrumb: "Thalamos Admin - London Care Record",
    buildFn: () => `/admin/london-care-record-configuration`,
  },
  fhirApiClientsPage: {
    path: "/admin/fhir-api-clients",
    exact: true,
    breadcrumb: "Thalamos Admin - FHIR API Clients",
    buildFn: () => `/admin/fhir-api-clients`,
  },
};

// These routes have different signatures to the others
export const rawRoutes = {
  ...rawRoutesBase,

  contextSelector: {
    path: "/context-selector",
    exact: true,
    breadcrumb: "Select Context",
    buildFn: (returnTo?: string) =>
      returnTo
        ? `/context-selector?returnTo=${encodeURIComponent(returnTo)}`
        : "/context-selector",
  },
  clickThroughLaunch: {
    path: "/clickthrough-launch",
    exact: true,
    breadcrumb: "Signing in",
    buildFn: (token: string, source: string) =>
      `/clickthrough-launch?token=${encodeURIComponent(token)}&source=${encodeURIComponent(source)}`,
  },
};

type RouteKeys = keyof typeof rawRoutes;

export const routeFns = Object.fromEntries(
  Object.entries(rawRoutes).map(([k, v]) => [k as RouteKeys, v.buildFn]),
) as {
  [k in RouteKeys]: (typeof rawRoutes)[k]["buildFn"];
};

/**
 * Keys of routes in rawRoutesBase where a version-mismatch reload is safe
 * (i.e. the user is not mid-flow and a reload will not lose unsaved work).
 *
 * The `satisfies` constraint ensures TypeScript errors if a key is renamed
 * or removed from rawRoutesBase. Keep in sync with RELOAD_SAFE_ROUTE_PATHS in
 * reloadSafeRoutePaths.ts (versionCheck.ts imports from there directly to avoid
 * a circular dependency: routes.ts → page components → api.ts → versionCheck.ts).
 */
const _RELOAD_SAFE_ROUTE_KEYS = [
  "home",
  "patientSearch",
  "patientHome",
  "patientEdit",
  "patientHomeActivityLog",
  "patientList",
  "caseloadUser",
  "caseloadCaseload",
  "caseloadArchive",
  "notifications",
  "userProfilePage",
  "managerUsersPage",
  "managerTeamsPage",
  "dashboard",
  "nhsDashboard",
  "dataGlossary",
  "careHomeRegister",
  "residentPage",
  "mhaStatusDashboardPage",
  "formContextPage",
  "formContextPageActivityLog",
] as const satisfies ReadonlyArray<keyof typeof rawRoutesBase>;
void _RELOAD_SAFE_ROUTE_KEYS;
